/**
 * The GTM container and the Consent Mode v2 handshake that has to precede it.
 *
 * <p>Basic Consent Mode: with only the necessary category granted the container is not injected at
 * all. Nothing is downloaded from googletagmanager.com, no cookie is written, and no ping goes out.
 * The alternative — loading the container and relying on denied consent signals — still fetches
 * third-party script and still sends cookieless pings, which is not what "kun nødvendig" promises.</p>
 *
 * <p>Ordering matters and is the reason this is a module of its own. GTM replays whatever is already
 * in the dataLayer when it boots, so the consent defaults have to be in the array before the
 * container script is appended; a default that lands afterwards applies to nothing that already
 * fired. The sequence here is therefore fixed: denied defaults, then the actual state, then the
 * container.</p>
 *
 * <p>The same ordering argument brings the central-measurement configuration here rather than into
 * the event envelope: the container has to know whether — and where — to tag a second property
 * before it evaluates its first tag. See <c>pushCentralAnalyticsConfig</c>.</p>
 *
 * <p>There is no noscript iframe. It cannot be gated on consent — markup renders whether or not the
 * visitor agreed — so a container that is conditional in script and unconditional in markup is not
 * conditional at all.</p>
 */

import { centralAnalyticsEnabled, centralGa4MeasurementId } from "./config";

/** The two optional categories, structurally — whatever the CMP stores, these are the fields read. */
export interface ConsentSignals {
  statistics: boolean;
  marketing: boolean;
}

/** Marks the script this module appends, so a hot reload does not add a second container. */
const SCRIPT_MARKER = "data-lob-gtm";

/**
 * Consent Mode v2, everything off. <c>security_storage</c> is the one exception: it covers
 * authentication and fraud prevention, is not a choice a visitor is asked to make, and Google treats
 * it as granted by definition.
 *
 * <p>No <c>wait_for_update</c>. It exists for containers that load before the CMP has resolved, and
 * buys that race by delaying tags. Here the update is pushed before the container is appended, so
 * there is no race to buy and the delay would be pure latency.</p>
 */
const CONSENT_DEFAULTS: Record<string, string> = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  functionality_storage: "denied",
  personalization_storage: "denied",
  security_storage: "granted",
};

/**
 * The container has been appended to this document. Module-level rather than a DOM lookup because a
 * second container is not merely wasteful: it double-fires every tag, and duplicated conversions are
 * the kind of error that is only discovered in the reporting weeks later.
 */
let injected = false;

/** The last state pushed, so a re-render that recomputes the same consent does not push it again. */
let lastUpdate: string | null = null;

// ── dataLayer ────────────────────────────────────────────────────────────────

/**
 * The dataLayer is created on first write and never before. An empty array is harmless in itself,
 * but creating it eagerly invites the assumption that it is safe to write to, and the one rule this
 * layer exists to keep is that nothing behavioural is in the array before consent.
 */
function dataLayer(): unknown[] {
  const w = window as unknown as { dataLayer?: unknown[] };
  if (!w.dataLayer) w.dataLayer = [];
  return w.dataLayer;
}

/** Append one entry. Callers are responsible for having checked consent, group and configuration. */
export function pushToDataLayer(entry: Record<string, unknown>): void {
  dataLayer().push(entry);
}

/**
 * Push a gtag command. Google's stub pushes the <c>arguments</c> object rather than an array, and
 * the container distinguishes the two, so the shape is reproduced exactly instead of approximated.
 */
function pushCommand(command: string, action: string, payload: Record<string, unknown>): void {
  const dl = dataLayer();
  function gtag() {
    dl.push(arguments);
  }
  (gtag as unknown as (...args: unknown[]) => void)(command, action, payload);
}

// ── Consent ──────────────────────────────────────────────────────────────────

/**
 * Map the two categories the banner offers onto the seven signals Consent Mode names. Statistics
 * carries analytics storage; marketing carries the advertising signals and the personalisation that
 * follows from them. Functionality is the necessary category, which is granted by definition.
 */
function toConsentState(consent: ConsentSignals): Record<string, string> {
  const marketing = consent.marketing ? "granted" : "denied";
  return {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    personalization_storage: marketing,
    analytics_storage: consent.statistics ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  };
}

/**
 * Report a consent change to an already-loaded container — a visitor who narrows or widens their
 * choice from the settings panel. Withdrawal has to travel too, which is why this is not gated on
 * anything being granted.
 */
export function pushConsentUpdate(consent: ConsentSignals): void {
  if (!injected) return;
  const next = toConsentState(consent);
  const fingerprint = JSON.stringify(next);
  if (fingerprint === lastUpdate) return;
  lastUpdate = fingerprint;
  pushCommand("consent", "update", next);
}

// ── Central measurement ──────────────────────────────────────────────────────

/**
 * The dataLayer keys the container's central GA4 tags read. Written once, before the container
 * boots, and never again.
 *
 * <p>They are configuration rather than event data, which is why they are not on the envelope every
 * event carries: the answer is the same for every event of a page load, and repeating it on each
 * push would invite a call site to vary it. GTM replays the array on boot and keeps what it finds in
 * its data model, so one push before the container script is what every later tag evaluates
 * against.</p>
 *
 * <p>The measurement id travels through here rather than being written into the container as a
 * constant on purpose. A constant would be a second place where LOBAS' property number is recorded,
 * kept in step with the register by hand — the exact duplication that was removed from the tenant
 * blocks, re-created one layer down.</p>
 */
const CENTRAL_ENABLED_KEY = "central_analytics_enabled";
const CENTRAL_MEASUREMENT_ID_KEY = "central_ga4_measurement_id";

/**
 * Announce the central destination to the container.
 *
 * <p>Both keys are always written, including when there is nothing to send. An absent key would
 * leave the container's variable undefined, and "undefined" is the same thing a stale page and a
 * broken load produce — whereas <c>false</c> is a statement. The id is written as an empty string
 * for the same reason, and the container's guard rejects it.</p>
 */
function pushCentralAnalyticsConfig(): void {
  const id = centralGa4MeasurementId();
  pushToDataLayer({
    [CENTRAL_ENABLED_KEY]: centralAnalyticsEnabled() && id !== null,
    [CENTRAL_MEASUREMENT_ID_KEY]: id ?? "",
  });
}

// ── Container ────────────────────────────────────────────────────────────────

/**
 * Bring the container up if consent allows it, and report whether it is there.
 *
 * <p>Returns false — and touches nothing at all, not even the dataLayer — while both optional
 * categories are refused. Returns true once the container has been appended, on this call or an
 * earlier one.</p>
 */
export function ensureGtmLoaded(containerId: string, consent: ConsentSignals): boolean {
  if (injected) return true;

  // Basic Consent Mode: necessary-only means no container, not a silenced one.
  if (!consent.statistics && !consent.marketing) return false;

  if (typeof document === "undefined") return false;

  // A hot reload re-runs this module with `injected` back to false while the old script is still in
  // the document. Adopt it rather than appending a twin.
  if (document.querySelector("script[" + SCRIPT_MARKER + "]")) {
    injected = true;
    return true;
  }

  // Defaults first, then the real state, then the container — see the note at the top of the file.
  pushCommand("consent", "default", CONSENT_DEFAULTS);
  const state = toConsentState(consent);
  lastUpdate = JSON.stringify(state);
  pushCommand("consent", "update", state);

  // Before gtm.start, so the central destination is in the data model the moment the container
  // evaluates its first tag rather than one event later.
  pushCentralAnalyticsConfig();

  pushToDataLayer({ "gtm.start": Date.now(), event: "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://www.googletagmanager.com/gtm.js?id=" + encodeURIComponent(containerId);
  script.setAttribute(SCRIPT_MARKER, containerId);
  document.head.appendChild(script);

  injected = true;
  return true;
}

/** Whether the container has been brought up in this document. */
export const gtmLoaded = (): boolean => injected;
